/**
 * Mood-to-Scale Mappings - Single Source of Truth
 *
 * This file handles the many-to-many relationship between moods and scales.
 * - Multiple moods can map to one scale
 * - One mood can map to multiple scales
 * - The first scale in each array is the primary (recommended) scale
 *
 * Separated from mode definitions to allow flexible UI-driven mappings
 * without duplicating music theory data.
 *
 * NOTE S7 Phase A : la donnee des moods vit dans la base (source unique). Le runtime lit
 * MOOD_MAPPINGS / MOOD_TO_SCALE / ALL_MOODS depuis le miroir (src/data/contentStore),
 * re-exportes ci-dessous.
 */

import { MOOD_MAPPINGS, MOOD_TO_SCALE, ALL_MOODS } from '../../data/contentStore';

export { MOOD_MAPPINGS, MOOD_TO_SCALE, ALL_MOODS };

/**
 * Mood mappings - each mood maps to an array of scales
 * The first scale in the array is the primary (recommended) scale
 */

/**
 * Get all unique mood names
 * @returns {string[]} - Array of all unique mood names
 */
export const getAllMoods = () => {
  return MOOD_MAPPINGS.map(m => m.mood);
};

/**
 * Get the primary scale for a mood (first scale in the array)
 * @param {string} mood - The mood to look up
 * @returns {string|null} - The primary scale name, or null if not found
 */
export const getPrimaryScaleForMood = (mood) => {
  const mapping = MOOD_MAPPINGS.find(m => m.mood === mood);
  return mapping ? mapping.scales[0] : null;
};

/**
 * Get all scales for a mood
 * @param {string} mood - The mood to look up
 * @returns {string[]} - Array of scale names (primary first)
 */
export const getAllScalesForMood = (mood) => {
  const mapping = MOOD_MAPPINGS.find(m => m.mood === mood);
  return mapping ? [...mapping.scales] : [];
};

/**
 * Get all moods that map to a given scale
 * @param {string} scaleName - The scale to look up
 * @returns {string[]} - Array of mood names
 */
export const getMoodsForScale = (scaleName) => {
  return MOOD_MAPPINGS
    .filter(m => m.scales.includes(scaleName))
    .map(m => m.mood);
};

/**
 * Get primary moods only (for UI dropdown)
 * Returns mood with its primary (first) scale
 * @returns {Array<{mood: string, scale: string}>} - Array of primary mood-scale pairs
 */
export const getPrimaryMoodMappings = () => {
  return MOOD_MAPPINGS.map(m => ({ mood: m.mood, scale: m.scales[0] }));
};

// MOOD_TO_SCALE et ALL_MOODS sont re-exportes depuis le miroir (cf haut du fichier).
// Le miroir les recalcule a chaque hydratation, donc ils refletent les deltas serveur.
